/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { URLProvidingEffectClass } from "types/AudioEffect";
import { Asset, AssetSet } from "types/Asset";
import { AssetId } from "@phonos/types";
import { PlaylistClip } from "types/Clip";
import gatewayFetch from "utilities/gatewayFetch";
import { IAssetUrlCache } from "types/AssetUrlCache";
import { ClipSkeletonWithAssetId } from "@phonos/types";

export class AssetUrlCache implements IAssetUrlCache {
  private assets: AssetSet = {};
  private assetUrlCache: Partial<Record<AssetId, Promise<string>>> = {};

  private localAudioAssetBlobUrls: Record<AssetId, string> = {};

  setAssets(assets: AssetSet): void {
    this.assets = assets;
    this.reset();
  }

  getAsset(assetId: AssetId): Asset {
    return this.assets[assetId];
  }

  async getClipUrl(
    clip: PlaylistClip,
    urlProvider: URLProvidingEffectClass
  ): Promise<string> {
    const asset = this.assets[clip.assetId];
    if (!asset) {
      throw new Error(`Asset not found: ${clip.assetId}`);
    }

    const expiringUrl = await this.getExpiringUrl(clip, asset, urlProvider);

    if (!expiringUrl && urlProvider.canUseLocalAsset() && asset.file) {
      let localUrl = this.localAudioAssetBlobUrls[asset.id];
      if (!localUrl) {
        localUrl = URL.createObjectURL(asset.file);
        this.localAudioAssetBlobUrls[asset.id] = localUrl;
      }
      // _findOrInitializeElementForClip uses some self-referential promises as it sets the clipAudio
      // this promise here lets those settle, otherwise some of the self references would be undefined
      await new Promise((resolve) => setTimeout(resolve, 0));
      return localUrl;
    }

    return expiringUrl;
  }

  // Tolerate any object that has an assetId property
  // This is so that we can invalidate the URL for a PlaylistClipForExport
  invalidateClipUrl(
    clip: ClipSkeletonWithAssetId,
    urlProvider: URLProvidingEffectClass
  ): void {
    const asset = this.assets[clip.assetId];
    delete this.assetUrlCache[this.getCacheKey(asset, urlProvider)];
    delete this.localAudioAssetBlobUrls[clip.assetId];
  }

  canLoadClip(clip: PlaylistClip): boolean {
    const asset = this.assets[clip.assetId];
    if (!asset) {
      throw new Error(`Asset not found: ${clip.assetId}`);
    }

    return !!asset.url || !!asset.file;
  }

  reset(): void {
    Object.values(this.localAudioAssetBlobUrls).forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    this.localAudioAssetBlobUrls = {};
  }

  private getCacheKey(
    asset: Asset,
    urlProvider: URLProvidingEffectClass
  ): string {
    return `${asset.id}-${urlProvider.name}`;
  }

  private async getExpiringUrl(
    clip: PlaylistClip,
    asset: Asset,
    urlProvider: URLProvidingEffectClass
  ): Promise<string> {
    const cacheKey = this.getCacheKey(asset, urlProvider);
    let existingUrlPromise = this.assetUrlCache[cacheKey];
    if (existingUrlPromise !== undefined) {
      return existingUrlPromise;
    }

    const nonExpiringUrl = urlProvider.providePermanentUrl(clip, asset);
    if (nonExpiringUrl) {
      this.assetUrlCache[cacheKey] = (async (): Promise<string> => {
        // Video files might not have a non-expiring URL yet if only just uploaded
        const expiringUrlResponse = await gatewayFetch(nonExpiringUrl!, {});
        const { url: expiringUrl } = await expiringUrlResponse.json();
        return expiringUrl;
      })();
    }

    return this.assetUrlCache[cacheKey] || "";
  }
}
